import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/auth";
import { formatEuro } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Check, X, Trash2, Filter, Mail, TrendingUp, Plus, Minus, Save, Edit2, Sliders, Palette, ShieldCheck, HelpCircle, Table as TableIcon, Settings2, Eye, Send, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import CabinetVisualizer from "@/components/CabinetVisualizer";
import { parsePricingWorkbook } from "@/lib/pricingGridParser";
import { Upload, Settings } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
  active: "bg-primary/10 text-primary",
  rejected: "bg-red-100 text-red-900 dark:bg-red-950/40 dark:text-red-300",
};
const STATUS_LABEL = { pending: "En attente", active: "Validé", rejected: "Refusé" };

// Matériaux de base des caissons / tiroirs pour l'édition de grille
const TIROIR_WIDTHS = [49, 69, 90];
const TIROIR_PROFONDEURS = [27, 30, 35, 40, 45, 50, 55];

const COMPONENT_PRICE_FIELDS = [
  { key: "PRIX_JOUE_M2", label: "Prix de la joue", unit: "€ HT / m²", group: "Structure", help: "Panneaux latéraux extérieurs + séparations internes (hauteur × profondeur)" },
  { key: "PRIX_DOS_M2", label: "Prix du dos", unit: "€ HT / m²", group: "Structure", help: "Fond du caisson, par section (largeur × hauteur)" },
  { key: "PRIX_CONNECTEUR_UNIT", label: "Prix du connecteur (Clamex)", unit: "€ HT / unité", group: "Structure", help: "Quantité = coins fixes du caisson + 1 par séparation interne" },
  { key: "CONNECTEUR_COINS_COUNT", label: "Nombre de coins par caisson", unit: "unités", group: "Structure", help: "Connecteurs fixes aux angles du caisson (hors séparations)" },
  { key: "PRIX_CHARNIERE_UNIT", label: "Prix de la charnière", unit: "€ HT / porte", group: "Quincaillerie", help: "Ajoutée automatiquement pour chaque section avec porte" },
  { key: "PRIX_COULISSE_TIROIR_UNIT", label: "Prix de la coulisse", unit: "€ HT / tiroir", group: "Quincaillerie", help: "Ajoutée automatiquement pour chaque tiroir" },
  { key: "PRIX_SEPARATION_QUINCAILLERIE_M2", label: "Séparation avec quincaillerie", unit: "€ HT / m²", group: "Quincaillerie", help: "Surcoût taquets/équerres par séparation interne, en plus du prix joue" },
  { key: "PRIX_PENDERIE_ML", label: "Prix de la penderie", unit: "€ HT / mL", group: "Aménagements", help: "Barre de penderie, au mètre linéaire de largeur de section" },
  { key: "PRIX_TABLETTE_FIXE_M2", label: "Prix tablette fixe", unit: "€ HT / m²", group: "Aménagements", help: "Étagère fixe (largeur × profondeur de la section)" },
  { key: "PRIX_TABLETTE_MOBILE_M2", label: "Prix tablette mobile", unit: "€ HT / m²", group: "Aménagements", help: "Étagère réglable (largeur × profondeur de la section)" },
  { key: "PRIX_MIROIR_M2", label: "Prix du miroir", unit: "€ HT / m²", group: "Aménagements", help: "Accessoire miroir glissé-déposé dans une section (largeur × hauteur choisies)" },
];

const HANDLE_SVG_TYPES = [
  { id: "push", name: "Push-to-Open (sans poignée)" },
  { id: "profil_line", name: "Profil Line (linéaire)" },
  { id: "etrier_square", name: "Étrier Square (anse)" },
  { id: "bouton_cyl", name: "Bouton Cylindrique" }
];

export default function AdminDashboard() {
  // Onglet courant
  const [activeTab, setActiveTab] = useState("users");

  // State utilisateurs (ancien AdminUsers)
  const [users, setUsers] = useState(null);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sending, setSending] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [allQuotes, setAllQuotes] = useState(null);
  const [clientCoefficients, setClientCoefficients] = useState({});
  const [savingCoefficientId, setSavingCoefficientId] = useState(null);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [remindingDraftId, setRemindingDraftId] = useState(null);

  // State settings d'administration
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Modale d'ajout/édition de poignée
  const [handleModalOpen, setHandleModalOpen] = useState(false);
  const [editingHandle, setEditingHandle] = useState(null); // null = ajout, obj = édition
  const [handleForm, setHandleForm] = useState({
    id: "",
    name: "",
    gamme: "",
    finish: "",
    swatch: "#151515",
    price: 15.0,
    svg_type: "etrier_square"
  });

  // Modale d'ajout/édition de finition
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [editingFinish, setEditingFinish] = useState(null); // null = ajout, obj = édition
  const [finishForm, setFinishForm] = useState({
    id: "",
    name: "",
    price_per_m2: 93.5,
    swatch: "#FFFFFF",
    category: "unie",
    description: ""
  });

  // Sélection pour l'affichage de la grille de prix des caissons (profondeur)
  // Sélection pour la gamme de tiroirs (Merivo M ou E)
  const [tiroirGamme, setTiroirGamme] = useState("M");

  // Chantier 4 — Import Excel de la grille de prix (parsing SheetJS côté navigateur,
  // persistance dans settings.pricing_grid_import, utilisée par le moteur de prix backend).
  const [parsedGrid, setParsedGrid] = useState(null);
  const [importingGrid, setImportingGrid] = useState(false);
  // Chantier 6 — historique des imports (comparer l'évolution des prix dans le temps)
  const [gridHistory, setGridHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (settings?.pricing_grid_import && !parsedGrid) setParsedGrid(settings.pricing_grid_import);
  }, [settings]); // eslint-disable-line

  const loadGridHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/admin/pricing-grid-history");
      setGridHistory(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { loadGridHistory(); }, []); // eslint-disable-line

  const handleImportPricingFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingGrid(true);
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parsePricingWorkbook(buffer);
      setParsedGrid(parsed);
      setSettings((prev) => ({ ...(prev || {}), pricing_grid_import: parsed }));
      await api.put("/admin/settings", { pricing_grid_import: parsed });
      await api.post("/admin/pricing-grid-history", { filename: file.name, sheet_name: parsed.sheet_name, meta: parsed.meta, sections: parsed.sections });
      await loadGridHistory();
      toast.success(`Fichier analysé : ${parsed.meta.extracted_points_count} prix extraits sur ${parsed.meta.sections_found.length} sections détectées`);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la lecture du fichier Excel : " + (err.message || "format inattendu"));
    } finally {
      setImportingGrid(false);
      e.target.value = "";
    }
  };

  const handleRestoreHistory = async (entry) => {
    setRestoringId(entry.id);
    try {
      await api.post(`/admin/pricing-grid-history/${entry.id}/restore`);
      const { data } = await api.get("/admin/settings");
      setParsedGrid(data.pricing_grid_import || null);
      setSettings(data);
      toast.success(`Version du ${new Date(entry.imported_at).toLocaleString("fr-FR")} restaurée (${entry.filename})`);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la restauration de cette version");
    } finally {
      setRestoringId(null);
    }
  };

  // Charger les utilisateurs et stats
  const loadUsers = () => {
    const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
    api.get(`/admin/users${q}`).then((r) => {
      setUsers(r.data);
      setClientCoefficients(Object.fromEntries(r.data.map((user) => [user.id, String(user.admin_price_coefficient ?? 1)])));
    }).catch(() => toast.error("Erreur de chargement des pros"));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const loadDrafts = () => {
    api.get("/admin/quote-drafts").then((r) => setDrafts(r.data)).catch(() => toast.error("Erreur de chargement du suivi commercial"));
  };

  // Chantier 4.5 : vue admin de tous les devis (tous pros confondus) — /api/quotes
  // retourne déjà l'intégralité des devis pour un compte role=admin.
  const loadAllQuotes = () => {
    api.get("/quotes").then((r) => setAllQuotes(r.data)).catch(() => toast.error("Erreur de chargement des devis"));
  };

  const remindDraft = async (draft) => {
    setRemindingDraftId(draft.id);
    try {
      await api.post(`/admin/quote-drafts/${draft.id}/remind`);
      toast.success("Relance envoyée", { description: `Email envoyé à ${draft.contact_name || draft.company || "ce professionnel"}.` });
      loadDrafts();
    } catch (error) {
      toast.error("Relance impossible", { description: formatApiError(error) });
    } finally {
      setRemindingDraftId(null);
    }
  };

  // Charger les settings dynamiques
  const loadSettings = () => {
    setSettingsLoading(true);
    api.get("/admin/settings")
      .then((r) => {
        setSettings(r.data);
      })
      .catch((err) => {
        toast.error("Impossible de charger les paramètres d'administration");
        console.error(err);
      })
      .finally(() => setSettingsLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => {
    loadSettings();
  }, []); // eslint-disable-line

  useEffect(() => {
    loadDrafts();
  }, []); // eslint-disable-line

  useEffect(() => {
    loadAllQuotes();
  }, []); // eslint-disable-line

  const doUserAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!confirm("Confirmez-vous la suppression définitive de ce professionnel ?")) return;
        await api.delete(`/admin/users/${id}`);
      } else {
        await api.post(`/admin/users/${id}/${action}`);
      }
      toast.success("Action effectuée avec succès");
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err) || "Une erreur est survenue");
    }
  };

  const saveClientCoefficient = async (userId) => {
    const value = parseFloat(clientCoefficients[userId]);
    if (isNaN(value) || value < 0.1 || value > 10) {
      toast.error("Le coefficient doit être compris entre 0,10 et 10,00");
      return;
    }
    setSavingCoefficientId(userId);
    try {
      await api.post(`/admin/users/${userId}/price-coefficient`, { admin_price_coefficient: value });
      toast.success("Coefficient client enregistré");
      loadUsers();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingCoefficientId(null);
    }
  };

  const sendWeekly = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-weekly-report");
      if (data.sent) toast.success("Rapport hebdomadaire envoyé aux administrateurs");
      else toast.error("Envoi refusé par le service de messagerie");
    } catch {
      toast.error("Erreur lors de l'envoi du rapport");
    } finally { setSending(false); }
  };

  // Sauvegarde globale des settings. pricing_grid_import est exclu de ce payload (il n'est
  // écrit que par l'import/la restauration de grille, jamais par cette sauvegarde générale).
  const saveAllSettings = async (updatedSettings = settings) => {
    setSavingSettings(true);
    try {
      const { pricing_grid_import, ...rest } = updatedSettings || {};
      const { data } = await api.put("/admin/settings", rest);
      setSettings((prev) => ({ ...data.settings, pricing_grid_import: pricing_grid_import ?? prev?.pricing_grid_import }));
      toast.success("Configurations enregistrées et appliquées en temps réel !");
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement : " + formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  // Modifier le coefficient de marge globale
  const handleMultiplierChange = (val) => {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      setSettings({ ...settings, pricing_multiplier: num });
    }
  };

  const adjustMultiplier = (amount) => {
    const current = settings?.pricing_multiplier || 1.0;
    const nextVal = Math.round((current + amount) * 100) / 100;
    setSettings({ ...settings, pricing_multiplier: Math.max(0.1, nextVal) });
  };

  // Modifier un tarif de composant (joue, dos, connecteur, quincaillerie, penderie, tablette)
  const handleComponentPriceChange = (key, val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    setSettings({
      ...settings,
      component_prices: { ...settings.component_prices, [key]: num }
    });
  };

  // Modifier un tarif de livraison (zone 50km / 100km)
  const handleDeliveryOptionChange = (key, val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    setSettings({
      ...settings,
      delivery_options: { ...settings.delivery_options, [key]: num }
    });
  };

  // Modifier une cellule de la matrice tiroirs
  const handleTiroirCellChange = (gamme, wIdx, pIdx, val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    const newMatrices = { ...settings.pricing_matrices };
    const key = gamme === "M" ? "TIROIR_M_PRICES" : "TIROIR_E_PRICES";
    const newTiroirPrices = [...newMatrices[key]];
    newTiroirPrices[wIdx] = newTiroirPrices[wIdx].map((cell, idx) => (idx === pIdx ? num : cell));
    newMatrices[key] = newTiroirPrices;
    setSettings({ ...settings, pricing_matrices: newMatrices });
  };

  // Modifier une cellule de la matrice de prix SdB (tiroirs x tranche de largeur x finition dessus)
  const handleSdbMatrixCellChange = (drawerKey, tierIdx, topFinish, val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    const newMatrices = { ...settings.pricing_matrices };
    const newSdbMatrix = { ...newMatrices.SDB_PRICE_MATRIX };
    const newTiers = [...newSdbMatrix[drawerKey]];
    newTiers[tierIdx] = { ...newTiers[tierIdx], [topFinish]: num };
    newSdbMatrix[drawerKey] = newTiers;
    newMatrices.SDB_PRICE_MATRIX = newSdbMatrix;
    setSettings({ ...settings, pricing_matrices: newMatrices });
  };

  // Modifier les options d'accessoires / pose
  const handleOptionChange = (key, val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    setSettings({
      ...settings,
      pricing_options: {
        ...settings.pricing_options,
        [key]: num
      }
    });
  };

  // POIGNÉES : Ajouter / Éditer / Supprimer
  const openHandleModal = (h = null) => {
    if (h) {
      setEditingHandle(h);
      setHandleForm({ ...h });
    } else {
      setEditingHandle(null);
      setHandleForm({
        id: "handle_" + Math.random().toString(36).slice(2, 8),
        name: "",
        gamme: "",
        finish: "",
        swatch: "#151515",
        price: 15.0,
        reference: "",
        svg_type: "etrier_square"
      });
    }
    setHandleModalOpen(true);
  };

  const saveHandleForm = () => {
    if (!handleForm.name || !handleForm.gamme || !handleForm.finish) {
      toast.error("Veuillez remplir tous les champs requis pour la poignée.");
      return;
    }
    let updatedHandles = [...settings.handles];
    if (editingHandle) {
      updatedHandles = updatedHandles.map((h) => (h.id === editingHandle.id ? handleForm : h));
    } else {
      // Éviter les IDs doublons
      if (updatedHandles.some((h) => h.id === handleForm.id)) {
        handleForm.id = handleForm.id + "_" + Math.random().toString(36).slice(2, 4);
      }
      updatedHandles.push(handleForm);
    }
    const updated = { ...settings, handles: updatedHandles };
    setSettings(updated);
    setHandleModalOpen(false);
    saveAllSettings(updated);
  };

  const deleteHandle = (handleId) => {
    if (!confirm("Confirmez-vous la suppression de cette poignée ?")) return;
    const updatedHandles = settings.handles.filter((h) => h.id !== handleId);
    const updated = { ...settings, handles: updatedHandles };
    setSettings(updated);
    saveAllSettings(updated);
  };

  // FINITIONS : Ajouter / Éditer / Supprimer
  const openFinishModal = (f = null) => {
    if (f) {
      setEditingFinish(f);
      setFinishForm({ ...f });
    } else {
      setEditingFinish(null);
      setFinishForm({
        id: "fin_" + Math.random().toString(36).slice(2, 8),
        name: "",
        price_per_m2: 93.5,
        swatch: "#CCCCCC",
        category: "unie",
        description: ""
      });
    }
    setFinishModalOpen(true);
  };

  const saveFinishForm = () => {
    if (!finishForm.name || !finishForm.id) {
      toast.error("Veuillez remplir le nom et l'identifiant de la finition.");
      return;
    }
    let updatedFinishes = [...settings.finishes];
    if (editingFinish) {
      updatedFinishes = updatedFinishes.map((f) => (f.id === editingFinish.id ? finishForm : f));
    } else {
      if (updatedFinishes.some((f) => f.id === finishForm.id)) {
        toast.error("Cet identifiant de finition existe déjà.");
        return;
      }
      updatedFinishes.push(finishForm);
    }
    const updated = { ...settings, finishes: updatedFinishes };
    setSettings(updated);
    setFinishModalOpen(false);
    saveAllSettings(updated);
  };

  const deleteFinish = (finishId) => {
    if (finishId === "blanc_premium") {
      toast.error("La finition de base 'Blanc Premium' ne peut pas être supprimée.");
      return;
    }
    if (!confirm("Confirmez-vous la suppression de cette finition ?")) return;
    const updatedFinishes = settings.finishes.filter((f) => f.id !== finishId);
    const updated = { ...settings, finishes: updatedFinishes };
    setSettings(updated);
    saveAllSettings(updated);
  };

  return (
    <main data-testid="admin-dashboard-page" className="w-full px-6 sm:px-10 lg:px-16 2xl:px-24 py-8 lg:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="eyebrow">Atelier Inter-Habitat</span>
            <span className="bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Espace Administrateur
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading">Panneau de Contrôle &amp; Tarifs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gérez vos utilisateurs et pilotez dynamiquement votre catalogue de quincailleries, finitions et grilles de prix.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <Button
            data-testid="send-weekly-report"
            variant="outline"
            onClick={sendWeekly}
            disabled={sending}
            className="text-xs h-10 flex-1 sm:flex-none"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Envoyer rapport hebdo
          </Button>
          <Button
            data-testid="admin-save-settings-btn"
            onClick={() => saveAllSettings()}
            disabled={settingsLoading || savingSettings}
            className="btn-primary text-xs h-10 gap-1.5 flex-1 sm:flex-none"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer les modifications
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <TabsList className="bg-card border border-border p-1 rounded-full grid w-full grid-cols-4 gap-1 sm:flex sm:w-auto">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full px-5 py-2 text-xs md:text-sm">
              Professionnels ({users ? users.length : 0})
            </TabsTrigger>
            <TabsTrigger value="followup" data-testid="admin-tab-followup" className="rounded-full px-5 py-2 text-xs md:text-sm">
              Suivi devis ({drafts ? drafts.length : 0})
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes" className="rounded-full px-5 py-2 text-xs md:text-sm">
              Tous les devis ({allQuotes ? allQuotes.length : 0})
            </TabsTrigger>
            <TabsTrigger value="rapports" data-testid="admin-tab-rapports" className="rounded-full px-5 py-2 text-xs md:text-sm">
              Rapports
            </TabsTrigger>
          </TabsList>
          {/* Réglages avancés (grilles de prix, finitions, poignées, options...) regroupés
              hors de la barre principale pour ne garder que les onglets opérationnels. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={["multiplier", "grids", "pricing-import", "finishes", "handles", "options"].includes(activeTab) ? "default" : "outline"}
                size="sm" data-testid="admin-settings-menu-trigger" className="rounded-full h-9 gap-1.5 text-xs"
              >
                <Settings className="w-3.5 h-3.5" /> Paramètres
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" data-testid="admin-settings-menu-content">
              <DropdownMenuLabel>Tarification</DropdownMenuLabel>
              <DropdownMenuItem data-testid="admin-settings-item-multiplier" onClick={() => setActiveTab("multiplier")}>Coefficient &amp; Marge</DropdownMenuItem>
              <DropdownMenuItem data-testid="admin-settings-item-grids" onClick={() => setActiveTab("grids")}>Grilles de Prix</DropdownMenuItem>
              <DropdownMenuItem data-testid="admin-settings-item-pricing-import" onClick={() => setActiveTab("pricing-import")}>Import Grille (Excel)</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Catalogue</DropdownMenuLabel>
              <DropdownMenuItem data-testid="admin-settings-item-finishes" onClick={() => setActiveTab("finishes")}>Finitions &amp; Décors</DropdownMenuItem>
              <DropdownMenuItem data-testid="admin-settings-item-handles" onClick={() => setActiveTab("handles")}>Quincaillerie / Poignées</DropdownMenuItem>
              <DropdownMenuItem data-testid="admin-settings-item-options" onClick={() => setActiveTab("options")}>Options &amp; SdB</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* --- ONGLET UTILISATEURS --- */}
        <TabsContent value="users" className="space-y-6">
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="admin-stats">
              <StatCard label="Pros validés" value={stats.active_users} />
              <StatCard label="En attente" value={stats.pending_users} highlight />
              <StatCard label="Brouillons à suivre" value={stats.active_drafts ?? 0} highlight />
              <StatCard label="Devis générés (7j)" value={stats.week_quotes} />
              <StatCard label="Nouveaux pros (7j)" value={stats.week_new_users} />
            </div>
          )}

          <div className="flex items-center justify-between gap-4 flex-wrap bg-card p-4 rounded-xl border border-border">
            <h2 className="text-lg font-heading flex items-center gap-2">Liste des comptes artisans</h2>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-filter" className="w-40 h-10 bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente de validation</SelectItem>
                  <SelectItem value="active">Comptes validés</SelectItem>
                  <SelectItem value="rejected">Comptes refusés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {!users ? (
            <div className="min-h-[30vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : users.length === 0 ? (
            <div className="paper p-12 text-center">
              <p className="text-muted-foreground">Aucun utilisateur ne correspond à ce filtre.</p>
            </div>
          ) : (
            <div className="paper w-full max-w-full overflow-x-auto" data-testid="admin-users-table">
              <table className="w-full table-fixed text-[11px] sm:text-sm">
                <thead className="border-b border-border bg-background/50">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="p-2 sm:p-4">Contact</th>
                    <th className="hidden md:table-cell p-4">Coef.</th>
                    <th className="p-2 sm:p-4">Société</th>
                    <th className="hidden sm:table-cell p-4">Email / Tél</th>
                    <th className="p-2 sm:p-4">Statut</th>
                    <th className="hidden lg:table-cell p-4">Inscription</th>
                    <th className="p-2 sm:p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                      <td className="p-2 sm:p-4 break-words">
                        <div className="font-medium text-foreground">{u.contact_name}</div>
                        {u.role === "admin" && <span className="bg-primary/10 text-primary text-[9px] font-bold px-1.5 py-0.5 rounded-full">Admin</span>}
                      </td>
                      <td className="hidden md:table-cell p-4">
                        {u.role !== "admin" ? (
                          <div className="flex items-center gap-1.5 min-w-[140px]">
                            <Input
                              data-testid={`client-coefficient-${u.id}`}
                              type="number"
                              min="0.1"
                              max="10"
                              step="0.01"
                              value={clientCoefficients[u.id] ?? "1"}
                              onChange={(e) => setClientCoefficients((current) => ({ ...current, [u.id]: e.target.value }))}
                              className="h-8 w-20 text-xs"
                            />
                            <Button data-testid={`save-client-coefficient-${u.id}`} size="sm" variant="outline" className="h-8 px-2" onClick={() => saveClientCoefficient(u.id)} disabled={savingCoefficientId === u.id}>
                              {savingCoefficientId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="p-2 sm:p-4 text-sm break-words">
                        <div className="font-medium">{u.company || "—"}</div>
                        <div className="text-xs text-muted-foreground">SIRET : {u.siret || "Non renseigné"}</div>
                      </td>
                      <td className="hidden sm:table-cell p-4 text-sm break-all">
                        <div className="text-foreground">{u.email}</div>
                        <div className="text-xs text-muted-foreground">{u.phone || "—"}</div>
                      </td>
                      <td className="p-2 sm:p-4">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-full ${STATUS_COLORS[u.status]}`}>
                          {STATUS_LABEL[u.status]}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell p-4 text-sm text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="p-2 sm:p-4">
                        <div className="flex items-center gap-1 justify-end">
                          {u.role !== "admin" && u.status !== "active" && (
                            <Button data-testid={`approve-${u.id}`} variant="ghost" size="sm" onClick={() => doUserAction(u.id, "approve")} className="text-primary hover:text-primary hover:bg-primary/5 h-8">
                              <Check className="w-4 h-4 mr-1" /> Valider
                            </Button>
                          )}
                          {u.role !== "admin" && u.status !== "rejected" && (
                            <Button data-testid={`reject-${u.id}`} variant="ghost" size="sm" onClick={() => doUserAction(u.id, "reject")} className="text-destructive hover:text-destructive hover:bg-destructive/5 h-8">
                              <X className="w-4 h-4 mr-1" /> Refuser
                            </Button>
                          )}
                          {u.role !== "admin" && (
                            <Button data-testid={`delete-${u.id}`} variant="ghost" size="icon" onClick={() => doUserAction(u.id, "delete")} className="text-muted-foreground hover:text-destructive h-8 w-8">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="followup" className="space-y-5">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-heading">Suivi commercial des projets</h2>
              <p className="text-xs text-muted-foreground mt-1">Configurations commencées ou enregistrées par les professionnels, sans affichage de prix.</p>
            </div>
            <Button data-testid="refresh-quote-drafts" variant="outline" size="sm" onClick={loadDrafts}>Actualiser</Button>
          </div>
          {!drafts ? <div className="min-h-[20vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : drafts.length === 0 ? (
            <div data-testid="quote-drafts-empty" className="paper p-10 text-center text-sm text-muted-foreground">Aucun projet en attente de devis.</div>
          ) : (
            <div className="paper w-full max-w-full overflow-x-auto" data-testid="quote-drafts-table">
              <table className="w-full min-w-[940px]">
                <thead className="border-b border-border bg-background/50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="p-4">Professionnel</th><th className="p-4">Projet</th><th className="p-4">Configuration</th><th className="p-4">État</th><th className="p-4">Dernière activité</th><th className="p-4 text-right">Actions</th></tr></thead>
                <tbody>{drafts.map((draft) => (
                  <tr key={draft.id} data-testid={`quote-draft-row-${draft.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                    <td className="p-4 text-sm"><div className="font-medium">{draft.contact_name || "—"}</div><div className="text-xs text-muted-foreground">{draft.company || draft.email}</div></td>
                    <td className="p-4 text-sm font-medium">{draft.project_name || "Sans nom"}</td>
                    <td className="p-4 text-xs text-muted-foreground">{draft.config.total_width * 10} × {draft.config.height * 10} × {draft.config.depth * 10} mm · {draft.config.sections.length} section{draft.config.sections.length > 1 ? "s" : ""}</td>
                    <td className="p-4"><span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-900">{draft.state === "saved" ? "Enregistré" : "Commencé"}</span></td>
                    <td className="p-4 text-sm text-muted-foreground"><div>{new Date(draft.updated_at).toLocaleString("fr-FR")}</div>{draft.last_reminder_at && <div className="mt-1 text-[10px] text-primary">Relancé le {new Date(draft.last_reminder_at).toLocaleDateString("fr-FR")}</div>}</td>
                    <td className="p-4 text-right"><div className="flex justify-end gap-2"><Button data-testid={`preview-quote-draft-${draft.id}`} size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setPreviewDraft(draft)}><Eye className="w-3.5 h-3.5" /> Voir le projet</Button><Button data-testid={`remind-quote-draft-${draft.id}`} size="sm" className="h-8 gap-1.5" onClick={() => remindDraft(draft)} disabled={remindingDraftId === draft.id}>{remindingDraftId === draft.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Relancer</Button></div></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="quotes" className="space-y-5">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-heading">Tous les devis — tous les professionnels</h2>
              <p className="text-xs text-muted-foreground mt-1">Vue d'ensemble des devis générés par l'ensemble des comptes clients.</p>
            </div>
            <Button data-testid="refresh-all-quotes" variant="outline" size="sm" onClick={loadAllQuotes}>Actualiser</Button>
          </div>
          {!allQuotes ? <div className="min-h-[20vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : allQuotes.length === 0 ? (
            <div data-testid="admin-quotes-empty" className="paper p-10 text-center text-sm text-muted-foreground">Aucun devis créé pour l'instant.</div>
          ) : (
            <div className="paper w-full max-w-full overflow-x-auto" data-testid="admin-quotes-table">
              <table className="w-full min-w-[980px]">
                <thead className="border-b border-border bg-background/50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="p-4">Référence</th><th className="p-4">Professionnel</th><th className="p-4">Client final</th><th className="p-4">Projet</th><th className="p-4">Date</th><th className="p-4 text-right">Total HT</th><th className="p-4 text-right">Actions</th></tr></thead>
                <tbody>{allQuotes.map((q) => (
                  <tr key={q.id} data-testid={`admin-quote-row-${q.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                    <td className="p-4 text-sm font-mono text-xs">{q.reference}</td>
                    <td className="p-4 text-sm"><div className="font-medium">{q.user_company || "—"}</div><div className="text-xs text-muted-foreground">{q.user_email || ""}</div></td>
                    <td className="p-4 text-sm"><div>{q.client?.contact_name || "—"}</div><div className="text-xs text-muted-foreground">{q.client?.company || q.client?.email || ""}</div></td>
                    <td className="p-4 text-sm">{q.project_name || "Sans nom"}</td>
                    <td className="p-4 text-sm text-muted-foreground">{new Date(q.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-4 text-right tabular-nums font-medium">{formatEuro(q.total_ht_all_items || q.breakdown?.total_ht)}</td>
                    <td className="p-4 text-right"><Link to={`/devis/${q.id}`} data-testid={`admin-view-quote-${q.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Voir <ArrowRight className="w-3 h-3" /></Link></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ─── ONGLET RAPPORTS ─────────────────────────────────────────────── */}
        <TabsContent value="rapports" className="space-y-5" data-testid="admin-tab-rapports-content">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-heading">Rapports de calcul — usage exclusif administration</h2>
              <p className="text-xs text-muted-foreground mt-1">Prix bruts, coefficients, marges, temps fabrication et surfaces matière. Ces données ne sont jamais transmises au client.</p>
            </div>
            <Button data-testid="refresh-rapports" variant="outline" size="sm" onClick={loadAllQuotes}>Actualiser</Button>
          </div>
          {!allQuotes ? <div className="min-h-[20vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : allQuotes.length === 0 ? (
            <div className="paper p-10 text-center text-sm text-muted-foreground">Aucun devis créé pour l'instant.</div>
          ) : (
            <div className="paper w-full max-w-full overflow-x-auto mb-4" data-testid="admin-rapports-table">
              <table className="w-full min-w-[980px]">
                <thead className="border-b border-border bg-background/50">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="p-4">Référence</th>
                    <th className="p-4">Pro</th>
                    <th className="p-4 text-right">Prix brut HT</th>
                    <th className="p-4 text-right">Coeff.</th>
                    <th className="p-4 text-right">Prix final HT</th>
                    <th className="p-4 text-right">Marge €</th>
                    <th className="p-4 text-right">Marge %</th>
                    <th className="p-4 text-right">Temps est.</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allQuotes.map((q) => {
                    const coeff = q.applied_price_coefficient || 1;
                    const finalHt = q.furniture_total_ht || q.total_ht_all_items || q.breakdown?.total_ht || 0;
                    const rawHt = coeff > 0 ? finalHt / coeff : finalHt;
                    const marge = finalHt - rawHt;
                    const margePct = rawHt > 0 ? (marge / rawHt) * 100 : 0;
                    // Time estimate — simple sum based on sections count + items
                    const nSections = (q.breakdown?.sections?.length || 1);
                    const nItems = (q.items?.length || 0);
                    const nSectionsItems = (q.items || []).reduce((s, it) => s + (it.breakdown?.sections?.length || 1), 0);
                    const totalSec = nSections + nSectionsItems;
                    const baseMin = 30 + totalSec * 10 + (nItems > 0 ? 45 : 0);
                    const hh = Math.floor(baseMin / 60), mm2 = baseMin % 60;
                    const timeStr = hh > 0 ? `${hh}h${mm2 > 0 ? String(mm2).padStart(2,'0') : ""}` : `${mm2} min`;
                    return (
                    <tr key={q.id} data-testid={`admin-rapport-row-${q.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                      <td className="p-4 text-xs font-mono">{q.reference}</td>
                      <td className="p-4 text-sm text-muted-foreground">{q.user_company || q.user_email || "—"}</td>
                      <td className="p-4 text-right tabular-nums text-sm text-muted-foreground">{formatEuro(rawHt)}</td>
                      <td className="p-4 text-right tabular-nums text-sm font-medium">{coeff.toFixed(2)}</td>
                      <td className="p-4 text-right tabular-nums text-sm font-semibold">{formatEuro(finalHt)}</td>
                      <td className="p-4 text-right tabular-nums text-sm">{formatEuro(marge)}</td>
                      <td className={`p-4 text-right tabular-nums text-sm font-semibold ${margePct >= 20 ? "text-green-700 dark:text-green-400" : margePct > 0 ? "text-amber-700 dark:text-amber-400" : "text-destructive"}`}>{margePct.toFixed(1)} %</td>
                      <td className="p-4 text-right text-sm text-muted-foreground">{timeStr}</td>
                      <td className="p-4 text-right"><Link to={`/devis/${q.id}`} data-testid={`admin-rapport-view-${q.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Fiche <ArrowRight className="w-3 h-3" /></Link></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <Dialog open={!!previewDraft} onOpenChange={(open) => !open && setPreviewDraft(null)}>
          <DialogContent data-testid="quote-draft-preview-modal" className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {previewDraft && <>
              <DialogHeader>
                <DialogTitle>{previewDraft.project_name || "Projet sans nom"}</DialogTitle>
                <DialogDescription>{previewDraft.contact_name || "Professionnel"}{previewDraft.company ? ` · ${previewDraft.company}` : ""} · aperçu non chiffré</DialogDescription>
              </DialogHeader>
              <div data-testid="quote-draft-visual-preview" className="mt-2 rounded-lg border border-border bg-background p-3 min-h-[360px]">
                <CabinetVisualizer
                  totalWidth={previewDraft.config.total_width}
                  height={previewDraft.config.height}
                  depth={previewDraft.config.depth}
                  sections={previewDraft.config.sections}
                  options={previewDraft.config.options}
                  carcassColor="#B08558"
                  doorsColor="#D9C6A5"
                  showDoors
                  interactive={false}
                  svgId={`draft-preview-svg-${previewDraft.id}`}
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-muted-foreground"><div><span className="block uppercase tracking-wider text-[10px]">Largeur</span>{previewDraft.config.total_width * 10} mm</div><div><span className="block uppercase tracking-wider text-[10px]">Hauteur</span>{previewDraft.config.height * 10} mm</div><div><span className="block uppercase tracking-wider text-[10px]">Profondeur</span>{previewDraft.config.depth * 10} mm</div><div><span className="block uppercase tracking-wider text-[10px]">Sections</span>{previewDraft.config.sections.length}</div></div>
            </>}
          </DialogContent>
        </Dialog>

        {/* --- ONGLET COEFFICIENT DE MARGE --- */}
        <TabsContent value="multiplier" className="space-y-6">
          <div className="paper p-6 max-w-2xl">
            <h2 className="text-xl font-heading mb-4 flex items-center gap-2">
              <Sliders className="w-5 h-5 text-primary" /> Multiplicateur Commercial Global
            </h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Ce coefficient est appliqué à <strong>l&apos;ensemble de la grille de prix</strong> (caissons, séparations, dos, tiroirs, penderies, tablettes, poignées) pour toutes les catégories de meubles (Dressing, Salle de Bain, Cuisine, Autres).
              <br /><br />
              <strong>Séparation stricte</strong> : ce coefficient ne s&apos;applique qu&apos;aux devis créés par l&apos;admin. Il n&apos;affecte jamais les prix du professionnel. Le pro a son propre coefficient de revente dans « Mon Compte ».
              <br /><br />
              - Un coefficient de <strong>1.00</strong> applique les prix de base du barème standard.
              <br />
              - Un coefficient de <strong>1.05</strong> applique une hausse globale de +5 % sur tous les tarifs.
              <br />
              - Un coefficient de <strong>0.98</strong> applique une baisse globale de -2 %.
            </p>

            {settingsLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <div className="space-y-4">
                <Label htmlFor="pricing_multiplier" className="text-sm font-bold">Coefficient multiplicateur actuel</Label>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => adjustMultiplier(-0.01)}
                    className="h-11 w-11 rounded-lg border-border"
                    data-testid="admin-mult-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    id="pricing_multiplier"
                    data-testid="admin-multiplier-input"
                    type="number"
                    step="0.01"
                    min="0.1"
                    max="10.0"
                    value={settings?.pricing_multiplier || 1.0}
                    onChange={(e) => handleMultiplierChange(e.target.value)}
                    className="h-11 text-center font-heading text-lg max-w-[150px] bg-background"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => adjustMultiplier(0.01)}
                    className="h-11 w-11 rounded-lg border-border"
                    data-testid="admin-mult-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-primary font-heading pl-2">
                    {settings?.pricing_multiplier > 1.0 
                      ? `(+${Math.round((settings.pricing_multiplier - 1.0) * 100)} % de hausse)`
                      : settings?.pricing_multiplier < 1.0 
                      ? `(-${Math.round((1.0 - settings.pricing_multiplier) * 100)} % de baisse)`
                      : "(Tarif de base exact)"}
                  </span>
                </div>

                <div className="pt-6 border-t border-border mt-6">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5" /> Note : Les modifications sont enregistrées dès que vous cliquez sur le bouton en haut à droite.
                  </p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* --- ONGLET FINITIONS --- */}
        <TabsContent value="finishes" className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" /> Tarifs par Finition &amp; Décor
              </h2>
              <p className="text-xs text-muted-foreground">
                Gérez les décors applicables aux caissons et façades ainsi que leur prix au m² (€ HT).
              </p>
            </div>
            <Button
              data-testid="finish-add-btn"
              onClick={() => openFinishModal()}
              className="btn-primary text-xs gap-1 h-9"
              disabled={settingsLoading}
            >
              <Plus className="w-4 h-4" /> Ajouter un décor
            </Button>
          </div>

          {settingsLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <div className="paper overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="border-b border-border bg-background/50">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="p-4">Rendu / Teinte</th>
                    <th className="p-4">Identifiant unique (ID)</th>
                    <th className="p-4">Nom commercial</th>
                    <th className="p-4">Catégorie</th>
                    <th className="p-4">Prix au m² (€ HT)</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.finishes.map((f) => (
                    <tr key={f.id} data-testid={`finish-row-${f.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-7 h-7 rounded-full border border-border shadow-sm" 
                            style={{ background: f.swatch }}
                            title={f.swatch}
                          />
                          <span className="text-xs font-mono">{f.swatch}</span>
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs">{f.id}</td>
                      <td className="p-4">
                        <div className="font-medium text-foreground">{f.name}</div>
                        <div className="text-xs text-muted-foreground">{f.description || "Aucune description"}</div>
                      </td>
                      <td className="p-4 text-sm capitalize">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          f.category === "unie" ? "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-300" :
                          f.category === "bois" ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300" :
                          "bg-purple-100 text-purple-900 dark:bg-purple-950 dark:text-purple-300"
                        }`}>
                          {f.category}
                        </span>
                      </td>
                      <td className="p-4 font-heading text-sm font-bold">
                        {(f.price_per_m2 ?? 93.5).toFixed(2)} € / m²
                        <span className="text-[10px] text-muted-foreground font-normal ml-1">
                          HT — portes &amp; caissons
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            data-testid={`edit-finish-${f.id}`} 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openFinishModal(f)} 
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            data-testid={`delete-finish-${f.id}`} 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => deleteFinish(f.id)} 
                            disabled={f.id === "blanc_premium"}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* --- ONGLET POIGNÉES / QUINCAILLERIE --- */}
        <TabsContent value="handles" className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-primary" /> Modèles de Poignées &amp; Quincaillerie
              </h2>
              <p className="text-xs text-muted-foreground">
                Ajoutez, modifiez ou retirez les poignées proposées dans le configurateur 2D/3D.
              </p>
            </div>
            <Button
              data-testid="handle-add-btn"
              onClick={() => openHandleModal()}
              className="btn-primary text-xs gap-1 h-9"
              disabled={settingsLoading}
            >
              <Plus className="w-4 h-4" /> Ajouter un modèle
            </Button>
          </div>

          {settingsLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <div className="paper overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="border-b border-border bg-background/50">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="p-4">Nuancier</th>
                    <th className="p-4">Identifiant (ID)</th>
                    <th className="p-4">Gamme</th>
                    <th className="p-4">Nom commercial</th>
                    <th className="p-4">Finition / Rendu</th>
                    <th className="p-4">Visuel 3D</th>
                    <th className="p-4">Tarif de base HT</th>
                    <th className="p-4">Référence</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.handles.map((h) => (
                    <tr key={h.id} data-testid={`handle-row-${h.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                      <td className="p-4">
                        <span 
                          className="w-6 h-6 rounded-full border border-border shadow-sm block" 
                          style={{ background: h.swatch }}
                          title={h.swatch}
                        />
                      </td>
                      <td className="p-4 font-mono text-xs">{h.id}</td>
                      <td className="p-4 text-sm font-semibold">{h.gamme}</td>
                      <td className="p-4 font-medium text-foreground">{h.name}</td>
                      <td className="p-4 text-sm">{h.finish}</td>
                      <td className="p-4 text-xs">
                        <span className="bg-primary/5 text-primary text-[10px] font-mono px-2 py-0.5 rounded border border-primary/10">
                          {h.svg_type || "—"}
                        </span>
                      </td>
                      <td className="p-4 font-heading text-sm font-bold text-foreground">
                        {h.price} € HT
                      </td>
                      <td className="p-4 text-xs font-mono text-muted-foreground" data-testid={`handle-reference-${h.id}`}>
                        {h.reference || "—"}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            data-testid={`edit-handle-${h.id}`} 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openHandleModal(h)} 
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            data-testid={`delete-handle-${h.id}`} 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => deleteHandle(h.id)} 
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* --- ONGLET GRILLES DE PRIX --- */}
        <TabsContent value="grids" className="space-y-6">
          <div className="paper p-6">
            <h2 className="text-xl font-heading mb-4 flex items-center gap-2">
              <TableIcon className="w-5 h-5 text-primary" /> Édition Directe des Barèmes Chiffrage (HT)
            </h2>
            <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
              Ces barèmes représentent les prix bruts HT du meuble hors coefficient d&apos;ajustement. Vous pouvez éditer les tarifs de chaque cellule du barème en euros HT. Les modifications sont appliquées immédiatement lors du clic sur le bouton de sauvegarde global.
            </p>

            {settingsLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <Tabs defaultValue="tiroirs" className="space-y-6">
                <TabsList className="bg-background border border-border p-0.5 rounded-lg flex w-max gap-1">
                  <TabsTrigger value="tiroirs" className="rounded-md px-4 py-1.5 text-xs">Tiroirs Merivo</TabsTrigger>
                  <TabsTrigger value="livraison" className="rounded-md px-4 py-1.5 text-xs">Livraison</TabsTrigger>
                </TabsList>

                {/* SOUS-ONGLET TIROIRS */}
                <TabsContent value="tiroirs" className="space-y-4">
                  <div className="flex items-center justify-between gap-4 flex-wrap bg-background p-3 rounded-lg border border-border">
                    <Label className="text-sm font-bold">Sélectionner la gamme de tiroir</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={tiroirGamme === "M" ? "default" : "outline"}
                        onClick={() => setTiroirGamme("M")}
                        className="h-8 text-xs px-4"
                      >
                        Merivobox M (Côté bas / H91)
                      </Button>
                      <Button
                        type="button"
                        variant={tiroirGamme === "E" ? "default" : "outline"}
                        onClick={() => setTiroirGamme("E")}
                        className="h-8 text-xs px-4"
                      >
                        Merivobox E (Côté haut / H192)
                      </Button>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-w-full border border-border rounded-lg bg-card">
                    <table className="w-full text-xs text-center border-collapse">
                      <thead>
                        <tr className="bg-background border-b border-border">
                          <th className="p-3 font-bold border-r border-border text-left bg-background/50 sticky left-0 z-10">Largeur \ Profondeur (mm)</th>
                          {TIROIR_PROFONDEURS.map((p) => (
                            <th key={p} className="p-3 font-semibold font-heading">≤ {p * 10}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {TIROIR_WIDTHS.map((w, wIdx) => (
                          <tr key={w} className="border-b border-border hover:bg-background/30">
                            <td className="p-3 font-bold border-r border-border text-left bg-background sticky left-0 z-10">Section ≤ {w * 10} mm</td>
                            {TIROIR_PROFONDEURS.map((p, pIdx) => {
                              const cellValue = settings.pricing_matrices[tiroirGamme === "M" ? "TIROIR_M_PRICES" : "TIROIR_E_PRICES"][wIdx][pIdx];
                              return (
                                <td key={p} className="p-2 border-r border-border/40">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    data-testid={`drawer-grid-cell-${tiroirGamme}-${wIdx}-${pIdx}`}
                                    value={cellValue}
                                    onChange={(e) => handleTiroirCellChange(tiroirGamme, wIdx, pIdx, e.target.value)}
                                    className="w-20 h-8 text-center px-1 font-heading text-xs bg-background border-border/80"
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                {/* SOUS-ONGLET LIVRAISON */}
                <TabsContent value="livraison" className="space-y-4">
                  <p className="text-xs text-muted-foreground -mt-2">
                    Le client saisit son code postal au moment du devis ; la distance depuis l'atelier
                    (Cantaing-sur-Escaut) est calculée automatiquement pour appliquer la bonne zone tarifaire.
                    Au-delà de 100 km, la livraison n'est pas proposée.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                    <div className="bg-card border border-border rounded-lg p-3 space-y-1.5">
                      <Label htmlFor="delivery-50km" className="text-xs font-semibold">Livraison ≤ 50 km</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="delivery-50km"
                          data-testid="delivery-price-50km"
                          type="number"
                          step="1"
                          min="0"
                          value={settings.delivery_options?.PRICE_TIER_50KM ?? ""}
                          onChange={(e) => handleDeliveryOptionChange("PRICE_TIER_50KM", e.target.value)}
                          className="h-9 bg-background"
                        />
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">€ HT</span>
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 space-y-1.5">
                      <Label htmlFor="delivery-100km" className="text-xs font-semibold">Livraison de 50 à 100 km</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="delivery-100km"
                          data-testid="delivery-price-100km"
                          type="number"
                          step="1"
                          min="0"
                          value={settings.delivery_options?.PRICE_TIER_100KM ?? ""}
                          onChange={(e) => handleDeliveryOptionChange("PRICE_TIER_100KM", e.target.value)}
                          className="h-9 bg-background"
                        />
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">€ HT</span>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </TabsContent>

        {/* --- ONGLET IMPORT GRILLE EXCEL (Chantier 4, étapes 1-3) --- */}
        <TabsContent value="pricing-import" className="space-y-6">
          <div className="paper p-6 space-y-5">
            <h2 className="text-xl font-heading mb-1 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" /> Mettre à jour la grille de prix (.xlsx)
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
              Importez le fichier de chiffrage (onglet &quot;Meuble&quot;). La lecture se fait directement dans votre
              navigateur (SheetJS) : chaque élément (Caisson, Séparation, Tablette, Porte...) est associé à sa
              matrice de dimensions ET à son tableau de quincaillerie situé à sa droite, sans mélanger les blocs
              entre eux. <strong>Cette grille alimente en direct le configurateur 3D</strong> (Caisson, Séparations
              et Portes) : un nouvel import met à jour les prix de vente dès l&apos;enregistrement.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-dashed border-primary/50 bg-primary/5 text-sm font-semibold cursor-pointer hover:bg-primary/10 transition-colors">
                <Upload className="w-4 h-4" />
                {importingGrid ? "Analyse en cours..." : "Choisir le fichier Excel (.xlsx)"}
                <input
                  type="file"
                  accept=".xlsx"
                  data-testid="pricing-import-file-input"
                  className="hidden"
                  disabled={importingGrid}
                  onChange={handleImportPricingFile}
                />
              </label>
              {parsedGrid && (
                <span className="text-xs text-muted-foreground" data-testid="pricing-import-summary">
                  Dernière analyse : {parsedGrid.meta.extracted_points_count} prix extraits · {parsedGrid.meta.sections_found.length} sections repérées
                </span>
              )}
            </div>

            {parsedGrid && (
              <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-background border-b border-border">
                      <th className="p-2.5 font-bold">Élément (section)</th>
                      <th className="p-2.5 font-bold">Blocs couplés reconnus</th>
                      <th className="p-2.5 font-bold">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(parsedGrid.meta.blocks_per_section).map(([section, count]) => (
                      <tr key={section} className="border-b border-border/40" data-testid={`pricing-import-row-${section}`}>
                        <td className="p-2.5 font-semibold">{section}</td>
                        <td className="p-2.5">{count}</td>
                        <td className="p-2.5">
                          {count > 0 ? (
                            <span className="text-emerald-700 dark:text-emerald-400 font-semibold">Reconnue</span>
                          ) : (
                            <span className="text-amber-700 dark:text-amber-400 font-semibold">À affiner (structure non couplée reconnue)</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* --- Chantier 6 : historique des imports (comparer l'évolution des prix) --- */}
            <div className="border-t border-border pt-5 space-y-3">
              <h3 className="text-sm font-heading">Historique des imports</h3>
              <p className="text-xs text-muted-foreground max-w-2xl">
                Chaque import est conservé pour pouvoir comparer les prix extraits au fil du temps, ou revenir à
                une version précédente de la grille.
              </p>
              {loadingHistory ? (
                <p className="text-xs text-muted-foreground">Chargement...</p>
              ) : gridHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="pricing-history-empty">Aucun import enregistré pour l'instant.</p>
              ) : (
                <div className="overflow-x-auto border border-border rounded-lg">
                  <table className="w-full text-xs text-left border-collapse">
                    <thead>
                      <tr className="bg-background border-b border-border">
                        <th className="p-2.5 font-bold">Date d'import</th>
                        <th className="p-2.5 font-bold">Fichier</th>
                        <th className="p-2.5 font-bold">Prix extraits</th>
                        <th className="p-2.5 font-bold">Importé par</th>
                        <th className="p-2.5 font-bold"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gridHistory.map((entry) => (
                        <tr key={entry.id} className="border-b border-border/40" data-testid={`pricing-history-row-${entry.id}`}>
                          <td className="p-2.5">{new Date(entry.imported_at).toLocaleString("fr-FR")}</td>
                          <td className="p-2.5 font-semibold">{entry.filename}</td>
                          <td className="p-2.5">{entry.meta?.extracted_points_count ?? "—"}</td>
                          <td className="p-2.5 text-muted-foreground">{entry.imported_by || "—"}</td>
                          <td className="p-2.5 text-right">
                            <Button
                              type="button" size="sm" variant="outline"
                              data-testid={`pricing-history-restore-${entry.id}`}
                              disabled={restoringId === entry.id}
                              onClick={() => handleRestoreHistory(entry)}
                            >
                              {restoringId === entry.id ? "Restauration..." : "Restaurer"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* --- ONGLET OPTIONS DE POSE & SDB --- */}
        <TabsContent value="options" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* OPTIONS DE POSE DÉDIÉES DRESSING / AUTRES */}
            <div className="paper p-6 space-y-4">
              <h2 className="text-lg font-heading border-b border-border pb-2">Tarification Options de Pose &amp; Habillage</h2>
              
              {settingsLoading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Prix du Fileur (Mètre Linéaire)</Label>
                      <span className="text-[10px] text-muted-foreground block">Appliqué sur la hauteur/largeur du fileur sélectionné</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.1"
                        data-testid="opt-fileur-input"
                        value={settings.pricing_options.PRICE_FILEUR_PER_ML}
                        onChange={(e) => handleOptionChange("PRICE_FILEUR_PER_ML", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">€</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Prix de la Plinthe (Mètre Linéaire)</Label>
                      <span className="text-[10px] text-muted-foreground block">Surcharge par ML si option plinthe cochée</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.1"
                        data-testid="opt-plinth-input"
                        value={settings.pricing_options.PRICE_PLINTH_PER_ML}
                        onChange={(e) => handleOptionChange("PRICE_PLINTH_PER_ML", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">€</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Remise Sans Fond (Forfaitaire)</Label>
                      <span className="text-[10px] text-muted-foreground block">Déduite du devis si option panneau arrière est désactivée</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.1"
                        data-testid="opt-no-back-input"
                        value={settings.pricing_options.PRICE_NO_BACK_DISCOUNT}
                        onChange={(e) => handleOptionChange("PRICE_NO_BACK_DISCOUNT", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">€</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Surcharge Portes Coulissantes (%)</Label>
                      <span className="text-[10px] text-muted-foreground block">Surcoût appliqué sur la portion portes (quincaillerie rails incluse)</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.01"
                        data-testid="opt-coulissant-input"
                        value={settings.pricing_options.PRICE_COULISSANT_SURCHARGE_PCT}
                        onChange={(e) => handleOptionChange("PRICE_COULISSANT_SURCHARGE_PCT", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">coeff</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Surcharge Complexité Portes Différentes (%)</Label>
                      <span className="text-[10px] text-muted-foreground block">Appliquée si le décor des portes diffère du caisson</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.01"
                        data-testid="opt-diff-doors-input"
                        value={settings.pricing_options.PRICE_DOORS_DIFFERENTIATION_SURCHARGE_PCT}
                        onChange={(e) => handleOptionChange("PRICE_DOORS_DIFFERENTIATION_SURCHARGE_PCT", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">coeff</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Prix de Base Porte (par m²)</Label>
                      <span className="text-[10px] text-muted-foreground block">Calcule la base de prix brute pour une façade (m²)</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.1"
                        data-testid="opt-porte-m2-input"
                        value={settings.pricing_options.PORTE_PRICE_PER_M2_HT}
                        onChange={(e) => handleOptionChange("PORTE_PRICE_PER_M2_HT", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">€</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* OPTIONS SALLE DE BAIN */}
            <div className="paper p-6 space-y-4">
              <h2 className="text-lg font-heading border-b border-border pb-2">Salle de Bain : Accessoires &amp; Colonnes</h2>
              
              {settingsLoading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-bold block mb-1">Matrice Prix Meubles Tiroirs (par largeur &amp; finition dessus)</Label>
                    <span className="text-[10px] text-muted-foreground block mb-2">Prix HT selon le nombre de tiroirs, la tranche de largeur et le dessus (sans vasque / avec dessus / avec vasque résine encastrée).</span>
                    <div className="space-y-3">
                      {[1, 2, 3].map((drawers) => (
                        <div key={drawers} className="overflow-x-auto border border-border rounded-lg bg-card">
                          <table className="w-full text-xs text-center border-collapse">
                            <thead>
                              <tr className="bg-background border-b border-border">
                                <th className="p-2 font-bold border-r border-border text-left bg-background/50 sticky left-0 z-10">{drawers} tiroir{drawers > 1 ? "s" : ""} — Largeur (mm)</th>
                                <th className="p-2 font-semibold">Sans vasque</th>
                                <th className="p-2 font-semibold">Avec dessus</th>
                                <th className="p-2 font-semibold">Avec vasque</th>
                              </tr>
                            </thead>
                            <tbody>
                              {["550–740", "750–940", "950–1200"].map((label, tierIdx) => (
                                <tr key={tierIdx} className="border-b border-border/60 hover:bg-background/30">
                                  <td className="p-2 font-bold border-r border-border text-left bg-background sticky left-0 z-10">{label}</td>
                                  {["sans_vasque", "avec_dessus", "avec_vasque"].map((finish) => (
                                    <td key={finish} className="p-1.5 border-r border-border/40">
                                      <Input
                                        type="number"
                                        step="1"
                                        data-testid={`sdb-matrix-cell-${drawers}-${tierIdx}-${finish}`}
                                        value={settings.pricing_matrices.SDB_PRICE_MATRIX?.[String(drawers)]?.[tierIdx]?.[finish] ?? 0}
                                        onChange={(e) => handleSdbMatrixCellChange(String(drawers), tierIdx, finish, e.target.value)}
                                        className="w-20 h-8 text-center px-1 font-heading text-xs bg-background border-border/80 mx-auto"
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Prix Fixe Colonne SdB 2 portes</Label>
                      <span className="text-[10px] text-muted-foreground block">Prix HT imposé de la colonne Salle de Bain 300x1400x200 mm</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="1"
                        data-testid="sdb-colonne-input"
                        value={settings.pricing_options.SDB_COLUMN_PRICE_HT}
                        onChange={(e) => handleOptionChange("SDB_COLUMN_PRICE_HT", e.target.value)}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">€</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Crédence Standard SdB (% du meuble)</Label>
                      <span className="text-[10px] text-muted-foreground block">Pourcentage par ML appliqué sur le meuble base (ex: 0.167 = 16.7%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.001"
                        data-testid="sdb-credence-input"
                        value={settings.pricing_matrices.CREDENCE_PCT_PER_ML}
                        onChange={(e) => {
                          const num = parseFloat(e.target.value);
                          if (!isNaN(num)) {
                            const newMatrices = { ...settings.pricing_matrices, CREDENCE_PCT_PER_ML: num };
                            setSettings({ ...settings, pricing_matrices: newMatrices });
                          }
                        }}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">coeff</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Crédence Soudée SdB (% du meuble)</Label>
                      <span className="text-[10px] text-muted-foreground block">Pourcentage par ML de crédence soudée (ex: 0.345 = 34.5%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.001"
                        data-testid="sdb-credence-soudee-input"
                        value={settings.pricing_matrices.CREDENCE_SOUDEE_PCT_PER_ML}
                        onChange={(e) => {
                          const num = parseFloat(e.target.value);
                          if (!isNaN(num)) {
                            const newMatrices = { ...settings.pricing_matrices, CREDENCE_SOUDEE_PCT_PER_ML: num };
                            setSettings({ ...settings, pricing_matrices: newMatrices });
                          }
                        }}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">coeff</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="max-w-[70%]">
                      <Label className="text-sm font-semibold block">Porte-Serviette SdB (% du meuble)</Label>
                      <span className="text-[10px] text-muted-foreground block">Pourcentage par porte-serviette (ex: 0.202 = 20.2%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input
                        type="number"
                        step="0.001"
                        data-testid="sdb-serviette-input"
                        value={settings.pricing_matrices.PORTE_SERVIETTE_PCT_PER_UNIT}
                        onChange={(e) => {
                          const num = parseFloat(e.target.value);
                          if (!isNaN(num)) {
                            const newMatrices = { ...settings.pricing_matrices, PORTE_SERVIETTE_PCT_PER_UNIT: num };
                            setSettings({ ...settings, pricing_matrices: newMatrices });
                          }
                        }}
                        className="w-24 h-9 bg-background text-right"
                      />
                      <span className="text-xs text-muted-foreground">coeff</span>
                    </div>
                  </div>

                  <div className="pt-2">
                    <Label className="text-sm font-bold block mb-2">Pieds Elliptiques SdB (Tarif Forfaitaire la Paire)</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {["100", "140", "200"].map((h) => (
                        <div key={h} className="bg-background p-2 rounded-lg border border-border flex flex-col gap-1 items-center">
                          <span className="text-[10px] text-muted-foreground font-semibold">H {h} mm</span>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              data-testid={`sdb-feet-input-${h}`}
                              value={settings.pricing_matrices.FOOT_ELLIPTIQUE_PRICES[h]}
                              onChange={(e) => {
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  const newPrices = { ...settings.pricing_matrices.FOOT_ELLIPTIQUE_PRICES, [h]: num };
                                  const newMatrices = { ...settings.pricing_matrices, FOOT_ELLIPTIQUE_PRICES: newPrices };
                                  setSettings({ ...settings, pricing_matrices: newMatrices });
                                }
                              }}
                              className="w-16 h-7 bg-card text-center p-0.5 text-xs"
                            />
                            <span className="text-[10px] text-muted-foreground">€</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* --- MODALE D&apos;AJOUT / ÉDITION POIGNÉE --- */}
      <Dialog open={handleModalOpen} onOpenChange={setHandleModalOpen}>
        <DialogContent data-testid="handle-form-dialog" className="sm:max-w-[450px] bg-card border border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {editingHandle ? "Modifier le modèle de poignée" : "Ajouter un modèle de poignée"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Configurez les propriétés visuelles et tarifaires de la poignée.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="handle_id" className="text-xs font-semibold">Identifiant unique (ID)</Label>
                <Input
                  id="handle_id"
                  data-testid="handle-input-id"
                  value={handleForm.id}
                  disabled={!!editingHandle}
                  onChange={(e) => setHandleForm({ ...handleForm, id: e.target.value })}
                  placeholder="profil_line_noir"
                  className="h-9 bg-background"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="handle_gamme" className="text-xs font-semibold">Gamme</Label>
                <Input
                  id="handle_gamme"
                  data-testid="handle-input-gamme"
                  value={handleForm.gamme}
                  onChange={(e) => setHandleForm({ ...handleForm, gamme: e.target.value })}
                  placeholder="Profil Line"
                  className="h-9 bg-background"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="handle_name" className="text-xs font-semibold">Nom complet</Label>
              <Input
                id="handle_name"
                data-testid="handle-input-name"
                value={handleForm.name}
                onChange={(e) => setHandleForm({ ...handleForm, name: e.target.value })}
                placeholder="Profil Line — Noir mat"
                className="h-9 bg-background"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="handle_finish" className="text-xs font-semibold">Finition</Label>
                <Input
                  id="handle_finish"
                  data-testid="handle-input-finish"
                  value={handleForm.finish}
                  onChange={(e) => setHandleForm({ ...handleForm, finish: e.target.value })}
                  placeholder="Noir mat"
                  className="h-9 bg-background"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="handle_price" className="text-xs font-semibold">Prix de base HT (€)</Label>
                <Input
                  id="handle_price"
                  data-testid="handle-input-price"
                  type="number"
                  step="0.1"
                  value={handleForm.price}
                  onChange={(e) => setHandleForm({ ...handleForm, price: parseFloat(e.target.value) || 0 })}
                  className="h-9 bg-background"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="handle_reference" className="text-xs font-semibold">Référence fournisseur</Label>
                <Input
                  id="handle_reference"
                  data-testid="handle-input-reference"
                  value={handleForm.reference || ""}
                  onChange={(e) => setHandleForm({ ...handleForm, reference: e.target.value })}
                  placeholder="Ex: BLUM-TTO-338"
                  className="h-9 bg-background"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="handle_swatch" className="text-xs font-semibold">Teinte Nuancier (Hex)</Label>
                <div className="flex gap-2">
                  <Input
                    id="handle_swatch"
                    data-testid="handle-input-swatch"
                    type="color"
                    value={handleForm.swatch}
                    onChange={(e) => setHandleForm({ ...handleForm, swatch: e.target.value })}
                    className="w-10 h-9 p-1 bg-background"
                  />
                  <Input
                    type="text"
                    value={handleForm.swatch}
                    onChange={(e) => setHandleForm({ ...handleForm, swatch: e.target.value })}
                    className="h-9 font-mono text-xs bg-background"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="handle_svg" className="text-xs font-semibold">Rendu visuel (Style SVG)</Label>
                <Select 
                  value={handleForm.svg_type} 
                  onValueChange={(val) => setHandleForm({ ...handleForm, svg_type: val })}
                >
                  <SelectTrigger data-testid="handle-input-svg" className="h-9 bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HANDLE_SVG_TYPES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setHandleModalOpen(false)} className="text-xs h-9">Annuler</Button>
            <Button data-testid="handle-save-btn" onClick={saveHandleForm} className="btn-primary text-xs h-9">
              {editingHandle ? "Enregistrer" : "Créer le modèle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- MODALE D&apos;AJOUT / ÉDITION FINITION --- */}
      <Dialog open={finishModalOpen} onOpenChange={setFinishModalOpen}>
        <DialogContent data-testid="finish-form-dialog" className="sm:max-w-[450px] bg-card border border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {editingFinish ? "Modifier la finition" : "Ajouter une finition"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Configurez le nom commercial, la teinte et le prix au m² de la finition.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="finish_id" className="text-xs font-semibold">Identifiant (ID unique)</Label>
                <Input
                  id="finish_id"
                  data-testid="finish-input-id"
                  value={finishForm.id}
                  disabled={!!editingFinish}
                  onChange={(e) => setFinishForm({ ...finishForm, id: e.target.value })}
                  placeholder="melamine_bleu"
                  className="h-9 bg-background"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="finish_category" className="text-xs font-semibold">Catégorie</Label>
                <Select 
                  value={finishForm.category} 
                  onValueChange={(val) => setFinishForm({ ...finishForm, category: val })}
                >
                  <SelectTrigger data-testid="finish-input-category" className="h-9 bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unie">Unie</SelectItem>
                    <SelectItem value="bois">Effet Bois / Placage</SelectItem>
                    <SelectItem value="special">Finition Spéciale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="finish_name" className="text-xs font-semibold">Nom commercial</Label>
              <Input
                id="finish_name"
                data-testid="finish-input-name"
                value={finishForm.name}
                onChange={(e) => setFinishForm({ ...finishForm, name: e.target.value })}
                placeholder="Mélaminé bleu marine"
                className="h-9 bg-background"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="finish_swatch" className="text-xs font-semibold">Teinte Nuancier (Hex)</Label>
                <div className="flex gap-2">
                  <Input
                    id="finish_swatch"
                    data-testid="finish-input-swatch"
                    type="color"
                    value={finishForm.swatch}
                    onChange={(e) => setFinishForm({ ...finishForm, swatch: e.target.value })}
                    className="w-10 h-9 p-1 bg-background"
                  />
                  <Input
                    type="text"
                    value={finishForm.swatch}
                    onChange={(e) => setFinishForm({ ...finishForm, swatch: e.target.value })}
                    className="h-9 font-mono text-xs bg-background"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="finish_price_per_m2" className="text-xs font-semibold">Prix au m² (€ HT)</Label>
                <Input
                  id="finish_price_per_m2"
                  data-testid="finish-input-price-per-m2"
                  type="number"
                  step="0.5"
                  min="0"
                  max="500"
                  value={finishForm.price_per_m2}
                  onChange={(e) => setFinishForm({ ...finishForm, price_per_m2: parseFloat(e.target.value) || 0 })}
                  className="h-9 bg-background"
                />
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5 shrink-0" /> Ce tarif s'applique à la surface des portes/façades ET des caissons dans cette finition.
            </p>

            <div className="space-y-1">
              <Label htmlFor="finish_description" className="text-xs font-semibold">Description courte</Label>
              <Input
                id="finish_description"
                data-testid="finish-input-description"
                value={finishForm.description}
                onChange={(e) => setFinishForm({ ...finishForm, description: e.target.value })}
                placeholder="Mélaminé mat velours, résistant"
                className="h-9 bg-background"
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setFinishModalOpen(false)} className="text-xs h-9">Annuler</Button>
            <Button data-testid="finish-save-btn" onClick={saveFinishForm} className="btn-primary text-xs h-9">
              {editingFinish ? "Enregistrer" : "Ajouter la finition"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`paper p-4 ${highlight && value > 0 ? "border-accent shadow-[0_0_12px_rgba(var(--accent-rgb),0.15)]" : "border-border"}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`font-heading text-3xl mt-1.5 tabular-nums ${highlight && value > 0 ? "text-accent font-bold animate-pulse" : "text-primary font-bold"}`}>{value}</p>
    </div>
  );
}
